import { Moment } from 'moment';

import { forTemplate } from 'app/common/lib/util/i18n';
const l = forTemplate('home');

export interface DueDateCardModel {
  boardId: string;
  cardId: string;
  dueComplete: boolean;
  dueDate: number;
  listId: string;
  orgId: string;
}

export type GroupName =
  | 'overdue'
  | 'due-today'
  | 'due-tomorrow'
  | 'due-week'
  | 'due-later'
  | 'none';

const getGroupName = (now: Moment, due: number): GroupName => {
  const withinNextDays = (days: number) =>
    !now.clone().isAfter(due) &&
    now
      .clone()
      .add(days, 'days')
      .endOf('day')
      .isAfter(due);

  const dueInLastDays = (days: number) =>
    now.clone().isAfter(due) &&
    now
      .clone()
      .subtract(days, 'days')
      .endOf('days')
      .isBefore(due);

  switch (true) {
    case dueInLastDays(14):
      return 'overdue';
    case withinNextDays(0):
      return 'due-today';
    case withinNextDays(1):
      return 'due-tomorrow';
    case withinNextDays(7):
      return 'due-week';
    case now.clone().isBefore(due):
      return 'due-later';
    default:
      return 'none';
  }
};

const inGroup = (now: Moment, due: number, groupName: GroupName) =>
  groupName === getGroupName(now, due);

interface Group {
  group: GroupName;
  name: string;
  cards: DueDateCardModel[];
  hide?: boolean;
}

export const getGroups = (
  dueDateCards: DueDateCardModel[],
  getDueDateOf: (dueDateCard: DueDateCardModel) => number,
  now: Moment,
): Group[] => {
  const groups: Group[] = [
    {
      group: 'overdue',
      name: l('due-in-last-14-days'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'overdue'),
      ),
      hide: !dueDateCards.some((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'overdue'),
      ),
    },
    {
      group: 'due-today',
      name: l('due-today'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-today'),
      ),
    },
    {
      group: 'due-tomorrow',
      name: l('due-tomorrow'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-tomorrow'),
      ),
    },
    {
      group: 'due-week',
      name: l('due-within-7-days'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-week'),
      ),
    },
    {
      group: 'due-later',
      name: l('due-later'),
      cards: dueDateCards.filter((dueDateCard) =>
        inGroup(now, getDueDateOf(dueDateCard), 'due-later'),
      ),
    },
  ];

  return groups.filter((group) => !group.hide);
};
